/**
 * پرسش‌های شفاف‌سازی، به‌شکل یک پیام گفت‌وگو.
 *
 * هر پرسش یک گزینه‌ی «اطلاعاتش را ندارم» دارد. آن گزینه `null` می‌فرستد، نه
 * رشته‌ی خالی: نامعلوم یک **پاسخ صریح** است که خط لوله آن را به فرض برچسب‌خورده
 * تبدیل می‌کند، در حالی که رشته‌ی خالی یعنی کاربر فیلد را رد کرده.
 *
 * پاسخ ندادن فرایند را متوقف نمی‌کند؛ دکمه همیشه فعال است.
 */

import { ArrowLeft, CircleHelp, SkipForward } from 'lucide-react'
import { useState } from 'react'

import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/cn'
import { fa } from '@/lib/format'

import type { ClarificationQuestion } from '@/lib/types'

interface ClarificationBubbleProps {
  questions: ClarificationQuestion[]
  isBusy: boolean
  /** پاسخ داده شده است؟ در آن حالت فقط‌خواندنی می‌شود. */
  answered: boolean
  /**
   * پاسخ حفظ‌شده‌ی هر پرسش از تلاش قبل، به ترتیب پرسش‌ها.
   *
   * ⚠️ بدون این، بازسازی همان پرسش‌ها را با فیلد خالی نشان می‌داد و کاربر
   * حق داشت بگوید «انگار یادش نیست جواب دادم».
   */
  prefill?: (string | null)[]
  /**
   * تعداد پاسخ‌هایی که حفظ شده‌اند اما این‌بار پرسیده نشده‌اند.
   *
   * ⚠️ شایع‌ترین حالت همین است: بررسی تازه پرسش‌های دیگری دارد، پس هیچ فیلدی
   * پیش‌پر نمی‌شود و صفحه سکوت می‌کرد — در حالی که واقعیت‌های دور قبل واقعاً در
   * زمینه حاضر بودند.
   */
  carriedCount?: number
  onSubmit: (answers: (string | null)[]) => void
}

export const ClarificationBubble = ({
  questions,
  isBusy,
  answered,
  prefill = [],
  carriedCount = 0,
  onSubmit,
}: ClarificationBubbleProps) => {
  const [answers, setAnswers] = useState<string[]>(() =>
    questions.map((_, index) => prefill[index] ?? ''),
  )
  const retainedCount = questions.filter(
    (_, index) => (prefill[index] ?? '') !== '',
  ).length

  const update = (index: number, value: string): void => {
    setAnswers((current) =>
      current.map((item, position) => (position === index ? value : item)),
    )
  }

  const submit = (skipAll: boolean): void => {
    onSubmit(
      questions.map((_, index) => {
        if (skipAll) {
          return null
        }
        const value = answers[index] ?? ''
        return value.trim() === '' ? null : value
      }),
    )
  }

  return (
    <div
      className={cn(
        'rounded-lg rounded-ss-sm border border-line bg-surface-raised shadow-raised',
        answered && 'opacity-70',
      )}
    >
      <div className="flex items-start gap-2.5 border-b border-line px-4 py-3">
        <CircleHelp className="mt-0.5 size-4 shrink-0 text-accent" aria-hidden />
        <div className="min-w-0 space-y-1">
          <p className="text-sm leading-relaxed text-ink">
            برای دقیق‌تر شدن برآورد، پاسخ این {fa(questions.length)} پرسش کمک می‌کند. اگر
            پاسخی ندارید رد کنید؛ فرایند متوقف نمی‌شود.
          </p>
          {retainedCount > 0 && (
            <p className="text-xs leading-relaxed text-ink-muted">
              {fa(retainedCount)} پاسخ قبلی شما حفظ شد و پیش‌پر شده است. فقط پرسش‌های
              خالی تازه‌اند.
            </p>
          )}
          {retainedCount === 0 && carriedCount > 0 && (
            <p className="text-xs leading-relaxed text-ink-muted">
              {fa(carriedCount)} پاسخ قبلی شما حفظ شد و در همین بررسی به کار می‌رود؛
              این‌بار پرسش‌های دیگری لازم است.
            </p>
          )}
        </div>
      </div>

      <div className="space-y-4 p-4">
        {questions.map((question, index) => {
          const inputId = `clarify-${String(index)}-${question.question.slice(0, 8)}`
          return (
            <div key={question.question} className="space-y-2">
              <label
                htmlFor={inputId}
                className="flex gap-2.5 text-sm leading-relaxed font-medium text-ink"
              >
                <span
                  className="grid size-5 shrink-0 place-items-center rounded-full bg-accent-soft text-[11px] font-bold tabular-nums text-accent"
                  aria-hidden
                >
                  {fa(index + 1)}
                </span>
                <span className="min-w-0">{question.question}</span>
              </label>
              {question.reason_it_matters !== '' && (
                <p className="ps-7.5 text-xs leading-relaxed text-ink-faint">
                  چرا مهم است: {question.reason_it_matters}
                </p>
              )}
              <input
                id={inputId}
                value={answers[index] ?? ''}
                disabled={answered || isBusy}
                onChange={(event) => {
                  update(index, event.target.value)
                }}
                placeholder="پاسخ شما — اختیاری"
                className="h-11 w-full rounded-md border border-line-strong bg-surface-raised px-3.5 text-sm text-ink shadow-raised transition-[border-color,box-shadow] placeholder:text-ink-faint focus:border-accent focus:ring-4 focus:ring-accent/15 focus:outline-none disabled:bg-surface-sunken disabled:text-ink-faint disabled:shadow-none"
              />
            </div>
          )
        })}

        {!answered && (
          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line pt-3">
            <Button
              variant="ghost"
              icon={<SkipForward className="size-4" aria-hidden />}
              disabled={isBusy}
              onClick={() => {
                submit(true)
              }}
            >
              پاسخی ندارم
            </Button>
            <Button
              variant="primary"
              icon={<ArrowLeft className="size-4" aria-hidden />}
              loading={isBusy}
              onClick={() => {
                submit(false)
              }}
            >
              ادامه و دریافت برآورد
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
